import React, { useEffect, useState } from "react";
import { BarChart3, Users, Database, CalendarDays, FileSpreadsheet } from "lucide-react";
import { API_BASE_URL, authFetch } from "./api";
import { VIDEO_REGRAS_DE_USO_ID } from "./config";
import VideoYouTube from "./components/VideoYouTube";

function formatDataCurta(iso) {
  if (!iso) return null;
  // "AAAA-MM-DD" e data de calendario: new Date() a le como UTC e, no fuso de Brasilia,
  // mostraria o dia anterior.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
  if (m) return `${m[3]}/${m[2]}`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

async function mensagemDeErro(res, prefixo) {
  // So texto vindo da API (JSON com detail/erro) aparece ao cliente; pagina HTML de proxy nunca.
  try {
    const body = await res.json();
    if (body && (body.detail || body.erro)) return body.detail || body.erro;
  } catch {
    // corpo nao e JSON
  }
  return `${prefixo} (erro ${res.status}).`;
}

// O arquivo expira em 7 dias (BaixarArquivo e limpar_mailings_antigos, no backend).
// Janela maior so mostraria linha sem download, entao nao ha periodo a escolher.
const DIAS_HISTORICO = 7;

const STATUS_LABEL = {
  PROCESSANDO: "Processando",
  SUCCESS: "Concluído",
  ERROR: "Erro",
  VAZIO: "Sem resultados",
};

export default function DashboardPage({ onNavigate }) {
  const [stats, setStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState("");

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");


  // Carregar resumo real do backend
  useEffect(() => {
    async function load() {
      try {
        setStatsLoading(true);
        const url = `${API_BASE_URL}/ia/dashboard-resumo/`;
        const res = await authFetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "include",
        });

        if (!res.ok) {
          throw new Error(await mensagemDeErro(res, "Não foi possível carregar os dados do dashboard"));
        }

        const data = await res.json();

        const usadosSemana = data.mailings_semana_usados ?? data.mailings_7d ?? 0;
        const limiteSemana = data.mailings_semana_limite ?? data.limite_semana ?? null;
        const restantesSemana = data.mailings_semana_restantes ?? data.restantes_semana ?? null;
        const isIlimitado = limiteSemana == null;
        const valorSemana = isIlimitado ? `${usadosSemana} / ∞` : `${usadosSemana}/${limiteSemana}`;
        const subSemana = isIlimitado
          ? "Acesso ilimitado. Sem restrição de extrações semanais."
          : `Você tem direito a realizar ${limiteSemana} extrações de leads por semana. Contabiliza de segunda a domingo e NÃO é cumulativo.`;

        const emAndamentoSemana = data.mailings_semana_em_andamento ?? 0;
        const sufixoEmAndamento = emAndamentoSemana > 0 ? ` · ${emAndamentoSemana} em andamento` : "";
        const renovaEm = formatDataCurta(data.reset_semana_em);

        const extraSemana = isIlimitado
          ? `Ilimitado${sufixoEmAndamento}`
          : `Restam: ${restantesSemana != null ? restantesSemana : "—"}${sufixoEmAndamento}${renovaEm ? ` · Renova em ${renovaEm}` : ""}`;

        const leadsTotal =
          data.leads_total ?? data.total_leads ?? data.leads_extraidos_total ?? data.leads_exportados_total ?? null;
        const leadsMes = data.leads_mes ?? null;
        const valorLeads = leadsTotal != null
          ? Number(leadsTotal).toLocaleString("pt-BR")
          : (data.leads_7d ?? 0).toLocaleString("pt-BR");
        const extraLeads = leadsMes != null ? `No mês: ${Number(leadsMes).toLocaleString("pt-BR")}` : "";

        const totalEmpresasBase =
          data.total_empresas_base ?? data.empresas_base_total ?? data.total_empresas ?? data.base_total_empresas ?? null;
        const valorEmpresas = totalEmpresasBase
          ? Number(totalEmpresasBase).toLocaleString("pt-BR")
          : "—";

        const ultimaAtualizacao =
          data.data_ultima_atualizacao_base ?? data.ultima_atualizacao_base ??
          data.data_atualizacao_base ?? data.base_atualizada_em ?? null;
        const valorAtualizacao = ultimaAtualizacao ? String(ultimaAtualizacao) : "—";

        const cards = [
          {
            key: "q1",
            label: "Seus Créditos Semanais",
            value: valorSemana,
            extra: extraSemana,
            help: subSemana,
            icon: BarChart3,
            highlight: true,
          },
          {
            key: "q2",
            label: "Seus LEADS extraídos",
            value: valorLeads,
            extra: extraLeads,
            help: "Este é o total de leads que você já extraiu dentro da nossa plataforma.",
            icon: Users,
          },
          {
            key: "q3",
            label: "Total de empresas em nossa base",
            value: valorEmpresas,
            extra: "",
            help: "Total de empresas atualizadas em nosso banco de dados.",
            icon: Database,
          },
          {
            key: "q4",
            label: "Data da última atualização da base",
            value: valorAtualizacao,
            extra: "",
            help: "Data da última atualização do nosso banco de dados com base na Receita Federal.",
            icon: CalendarDays,
          },
        ];

        setStats(cards);
        setError("");
      } catch (e) {
        setError(e.message || "Não foi possível carregar os dados do dashboard.");
      } finally {
        setStatsLoading(false);
      }
    }

    load();
  }, []);

  // Carregar histórico de listas geradas
  useEffect(() => {
    const controller = new AbortController();

    async function loadHistory() {
      try {
        setLoadingHistory(true);
        setHistoryError("");

        const url = `${API_BASE_URL}/ia/dashboard-historico/?dias=${DIAS_HISTORICO}&incluir_todos=1`;

        const res = await authFetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "include",
          signal: controller.signal,
        });

        if (!res.ok) {
          throw new Error(await mensagemDeErro(res, "Não foi possível carregar o histórico"));
        }

        const data = await res.json();
        const lista = Array.isArray(data) ? data : data.historico || [];

        const listaNormalizada = lista.map((item, index) => ({
          id: item.id ?? item.task_id ?? index,
          taskId: item.task_id ?? null,
          data: item.data,
          total_empresas: item.total_empresas ?? item.leads ?? 0,
          arquivo: item.arquivo ?? item.nome_arquivo ?? null,
          status: item.status ?? "SUCCESS",
          mensagemErro: item.mensagem_erro ?? null,
          arquivoDisponivel: item.arquivo_disponivel !== false,
        }));

        setHistory(listaNormalizada);
      } catch (e) {
        if (e.name === "AbortError") return;
        setHistoryError(e.message || "Não foi possível carregar o histórico.");
      } finally {
        if (!controller.signal.aborted) setLoadingHistory(false);
      }
    }

    loadHistory();
    return () => controller.abort();
  }, []);

  async function handleDownloadItem(item) {
    try {
      const url = item.taskId
        ? `${API_BASE_URL}/async/tasks/${item.taskId}/download/`
        : `${API_BASE_URL}/mailings/historico/${item.id}/download/`;

      const res = await authFetch(url, {
        method: "GET",
        headers: { Accept: "*/*" },
        credentials: "include",
      });

      if (!res.ok) {
        const text = await res.text();
        let msg = `Erro ao baixar (${res.status})`;
        try {
          const data = JSON.parse(text);
          msg = data.erro || data.detail || msg;
        } catch { /* body não é JSON */ }
        throw new Error(msg);
      }

      const blob = await res.blob();
      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = urlBlob;
      a.download = item.arquivo || `lista_empresas_${item.id}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(urlBlob);
    } catch (err) {
      setHistoryError(err.message || "Erro ao baixar a lista de empresas.");
    }
  }

  async function handleDownloadHistorico() {
    try {
      const url = `${API_BASE_URL}/ia/dashboard-historico.csv?dias=${DIAS_HISTORICO}`;

      const res = await authFetch(url, {
        method: "GET",
        headers: { Accept: "text/csv" },
        credentials: "include",
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Erro ao baixar histórico (${res.status}): ${text}`);
      }

      const blob = await res.blob();
      const urlBlob = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = urlBlob;
      link.setAttribute("download", `historico_listas_${DIAS_HISTORICO}dias.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(urlBlob);
    } catch (e) {
      setHistoryError(e.message || "Erro ao baixar o histórico.");
    }
  }

  return (
    <section className="result-card animate-fade-in-up">
      {error && <p className="error-text">{error}</p>}

      <div className="dash-video-block">
        <h3 className="dash-video-title">Regras de uso e como funciona</h3>
        <p className="muted small dash-video-sub">
          Assista antes de gerar sua primeira lista: são poucos minutos e evitam erro na extração.
        </p>
        <div className="dash-video-wrap">
          <VideoYouTube id={VIDEO_REGRAS_DE_USO_ID} titulo="Regras de uso da plataforma" />
        </div>
      </div>

      <div className="dash-grid-4">
        {statsLoading && stats.length === 0 ? (
          <p className="muted small">Carregando dados do dashboard...</p>
        ) : (
          (stats || []).map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.key} className={`dash-card ${s.highlight ? "highlight" : ""}`} title={s.help || undefined}>
                <div className="dash-card-top">
                  <span className="stat-label">{s.label}</span>
                  <div className="dash-card-actions">
                    {Icon && (
                      <div className="dash-icon-box">
                        <Icon size={16} />
                      </div>
                    )}
                  </div>
                </div>
                <span className="stat-value">{s.value}</span>
                {s.extra ? <div className="stat-extra">{s.extra}</div> : null}
              </div>
            );
          })
        )}
      </div>

      <div className="dash-bottom-grid">
        <div className="dash-card dash-history-card" style={{ gridColumn: "1 / -1" }}>
          <div className="dash-history-header">
            <div>
              <span className="stat-label">Histórico de bases geradas</span>
              <div className="stat-sub">
                Histórico dos últimos {DIAS_HISTORICO} dias
                {loadingHistory ? " • Carregando…" : ""}
              </div>
            </div>

            <div className="dash-history-actions">
              <button
                type="button"
                className="chip chip-primary"
                onClick={handleDownloadHistorico}
                title="Baixar histórico em CSV"
              >
                Baixar CSV
              </button>
            </div>
          </div>

          <div className="dash-aviso">
            Seu arquivo ficará disponível por <strong>7 dias</strong>. Faça o download e guarde seu arquivo.
          </div>

          {historyError && <p className="error-text">{historyError}</p>}

          {loadingHistory ? (
            <p className="muted small">Carregando histórico...</p>
          ) : history.length === 0 ? (
            <p className="muted small">
              Nenhuma lista de empresas gerada nos últimos {DIAS_HISTORICO} dias.
            </p>
          ) : (
            <div className="history-list">
              {history.map((item, index) => {
                const arquivo = item.arquivo || "-";
                const leads = item.total_empresas?.toLocaleString("pt-BR") || "0";
                const podeBaixar = item.status === "SUCCESS" && item.arquivoDisponivel;

                return (
                  <div className="history-item" key={item.id || index}>
                    <div className="history-left">
                      <div className="history-top">
                        <span className="history-date">{item.data}</span>
                        <span className="history-leads">{leads} leads</span>
                        <span
                          className="chip chip-primary"
                          style={item.status === "ERROR" ? { background: "rgba(220,38,38,0.12)", color: "#dc2626", border: "1px solid rgba(220,38,38,0.3)" } : undefined}
                        >
                          {STATUS_LABEL[item.status] || item.status}
                        </span>
                      </div>

                      <div className="history-file" title={arquivo}>
                        {arquivo}
                      </div>
                      {item.status === "ERROR" && item.mensagemErro && (
                        <div className="error-text small">{item.mensagemErro}</div>
                      )}
                    </div>

                    {podeBaixar ? (
                      <button
                        type="button"
                        className="chip chip-primary history-download"
                        onClick={() => handleDownloadItem(item)}
                      >
                        Baixar
                      </button>
                    ) : item.status === "SUCCESS" ? (
                      <span className="muted small">Arquivo expirado</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        className="dash-cta"
        onClick={() => onNavigate && onNavigate("mailing")}
      >
        <FileSpreadsheet size={28} />
        GERAR LISTA DE EMPRESAS
      </button>
    </section>
  );
}
